import { useState } from 'react';
import { useCashFlow } from '../../hooks/useReports';
import { Skeleton } from '@/shared/components/ui/Skeleton';
import { ChevronRight, ChevronDown, Calendar } from 'lucide-react';
import { Button } from '@/shared/components/ui/Button';

const FINANCIAL_YEARS = [
  { label: 'FY 2026-2027', startDate: '2026-04-01', endDate: '2027-03-31', displayDates: 'April 01 - March 31' },
  { label: 'FY 2025-2026', startDate: '2025-04-01', endDate: '2026-03-31', displayDates: 'April 01 - March 31' },
  { label: 'FY 2024-2025', startDate: '2024-04-01', endDate: '2025-03-31', displayDates: 'April 01 - March 31' },
];

interface CashFlowProps {
  startDate?: string;
  endDate?: string;
}

export function CashFlow({ startDate: propStartDate, endDate: propEndDate }: CashFlowProps) {
  const [selectedFY, setSelectedFY] = useState('FY 2026-2027');
  const [startDate, setStartDate] = useState(propStartDate || '2026-04-01');
  const [endDate, setEndDate] = useState(propEndDate || '2027-03-31');

  // Accordion states
  const [isOperatingOpen, setIsOperatingOpen] = useState(false);
  const [isInvestingOpen, setIsInvestingOpen] = useState(false);
  const [isFinancingOpen, setIsFinancingOpen] = useState(false);

  const { data, isLoading, error } = useCashFlow(startDate, endDate);

  const handleFYChange = (fyLabel: string) => {
    setSelectedFY(fyLabel);
    const fy = FINANCIAL_YEARS.find(f => f.label === fyLabel);
    if (fy) {
      setStartDate(fy.startDate);
      setEndDate(fy.endDate);
    }
  };

  const formatCurrency = (val: number | undefined | null) => {
    const num = val || 0;
    return `₹${Math.abs(num).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  if (isLoading) return <Skeleton className="h-[500px] w-full rounded-xl" />;
  if (error || !data) {
    return (
      <div className="p-6 text-center text-rose-600 bg-rose-50 rounded-xl border border-rose-200 text-sm font-medium">
        Failed to load Cash Flow Statement.
      </div>
    );
  }

  // Calculate categorized breakdowns from backend data
  const beginningBalance = data.openingCash || 0;
  const netIncome = data.operating?.netProfit || 0;

  // Operating items
  const opAdjustments = data.operating?.adjustments || [];
  const depreciation = opAdjustments.find(a => a.name?.toLowerCase().includes('deprec') || a.code?.startsWith('55'))?.amount || 0;
  const interestExpense = opAdjustments.find(a => a.name?.toLowerCase().includes('interest exp'))?.amount || 0;
  const interestIncome = opAdjustments.find(a => a.name?.toLowerCase().includes('interest inc'))?.amount || 0;
  const profitAssetSale = opAdjustments.find(a => a.name?.toLowerCase().includes('sale of fixed'))?.amount || 0;

  const totalAdjustments = depreciation + interestExpense + interestIncome + profitAssetSale;
  const opProfitBeforeWC = netIncome + totalAdjustments;

  const inventory = opAdjustments.find(a => a.name?.toLowerCase().includes('inventory') || a.code?.startsWith('13'))?.amount || 0;
  const receivables = opAdjustments.find(a => a.name?.toLowerCase().includes('receiv') || a.code?.startsWith('12'))?.amount || 0;
  const payables = opAdjustments.find(a => a.name?.toLowerCase().includes('payab') || a.code?.startsWith('21'))?.amount || 0;
  const taxPayable = opAdjustments.find(a => a.name?.toLowerCase().includes('tax payab'))?.amount || 0;
  const taxReceivable = opAdjustments.find(a => a.name?.toLowerCase().includes('tax receiv'))?.amount || 0;
  const provisions = opAdjustments.find(a => a.name?.toLowerCase().includes('provision'))?.amount || 0;
  const otherWC = opAdjustments.filter(a => 
    !a.name?.toLowerCase().includes('inventory') && 
    !a.name?.toLowerCase().includes('receiv') && 
    !a.name?.toLowerCase().includes('payab') && 
    !a.name?.toLowerCase().includes('tax') && 
    !a.name?.toLowerCase().includes('provision') &&
    !a.name?.toLowerCase().includes('deprec')
  ).reduce((s, a) => s + a.amount, 0);

  const totalWCChanges = inventory + receivables + payables + taxPayable + taxReceivable + provisions + otherWC;
  const cashFromOperations = opProfitBeforeWC + totalWCChanges;
  const operatingTotal = data.operating?.total || cashFromOperations;

  // Investing items
  const invItems = data.investing?.items || [];
  const ppePurchase = invItems.find(i => i.name?.toLowerCase().includes('ppe') || i.name?.toLowerCase().includes('plant') || i.name?.toLowerCase().includes('equipment'))?.amount || 0;
  const ppeSale = invItems.find(i => i.name?.toLowerCase().includes('sale') && i.name?.toLowerCase().includes('fixed'))?.amount || 0;
  const investmentsPurchase = invItems.find(i => i.name?.toLowerCase().includes('purchase') && i.name?.toLowerCase().includes('invest'))?.amount || 0;
  const investmentsSale = invItems.find(i => i.name?.toLowerCase().includes('sale') && i.name?.toLowerCase().includes('invest'))?.amount || 0;
  const interestReceived = invItems.find(i => i.name?.toLowerCase().includes('interest rec'))?.amount || 0;
  const dividendReceived = invItems.find(i => i.name?.toLowerCase().includes('dividend rec'))?.amount || 0;
  const investingTotal = data.investing?.total || (ppePurchase + ppeSale + investmentsPurchase + investmentsSale + interestReceived + dividendReceived);

  // Financing items
  const finItems = data.financing?.items || [];
  const shareCapital = finItems.find(i => i.name?.toLowerCase().includes('share') || i.name?.toLowerCase().includes('equity'))?.amount || 0;
  const borrowingsProceeds = finItems.find(i => i.name?.toLowerCase().includes('borrowing') || i.name?.toLowerCase().includes('loan'))?.amount || 0;
  const borrowingsRepayment = finItems.find(i => i.name?.toLowerCase().includes('repay'))?.amount || 0;
  const interestPaid = finItems.find(i => i.name?.toLowerCase().includes('interest paid'))?.amount || 0;
  const dividendPaid = finItems.find(i => i.name?.toLowerCase().includes('dividend paid'))?.amount || 0;
  const financingTotal = data.financing?.total || (shareCapital + borrowingsProceeds + borrowingsRepayment + interestPaid + dividendPaid);

  const netIncreaseInCash = data.netChangeInCash || (operatingTotal + investingTotal + financingTotal);
  const endingBalance = data.closingCash || (beginningBalance + netIncreaseInCash);

  return (
    <div className="space-y-6">
      {/* Filters Panel */}
      <div className="space-y-3">
        <h3 className="text-lg font-bold tracking-tight text-foreground">Filters</h3>
        <div className="flex flex-wrap items-end gap-4">
          <div className="flex flex-col">
            <label className="text-xs text-muted-foreground font-medium mb-1">Select Financial Year</label>
            <select
              value={selectedFY}
              onChange={(e) => handleFYChange(e.target.value)}
              className="border border-input rounded-md text-sm px-3 py-2 bg-background font-medium shadow-sm w-52"
            >
              {FINANCIAL_YEARS.map(fy => (
                <option key={fy.label} value={fy.label}>{fy.label}</option>
              ))}
            </select>
          </div>

          <div className="flex flex-col">
            <label className="text-xs text-muted-foreground font-medium mb-1">Select Dates <span className="text-red-500">*</span></label>
            <div className="relative">
              <input
                type="text"
                readOnly
                value={`${startDate} - ${endDate}`}
                className="border border-input rounded-md text-sm px-3 py-2 bg-background font-medium shadow-sm w-64 pr-9"
              />
              <Calendar className="w-4 h-4 text-muted-foreground absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none" />
            </div>
          </div>

          <Button
            variant="outline"
            className="border-purple-600 text-purple-600 hover:bg-purple-50 dark:hover:bg-purple-950/30 px-6 h-9 font-medium"
          >
            Apply
          </Button>
        </div>
      </div>

      {/* Main Cash Flow Table */}
      <div className="rounded-xl border border-border overflow-hidden shadow-sm bg-white dark:bg-slate-950">
        {/* Purple Table Header */}
        <div className="bg-purple-600 text-white px-6 py-3.5 flex justify-between items-center text-sm font-semibold tracking-wide">
          <span>Name</span>
          <span>Balance</span>
        </div>

        {/* Table Rows */}
        <div className="divide-y divide-border/60 text-sm">
          {/* 1. Beginning Cash Balance */}
          <div className="px-6 py-4 flex justify-between items-center font-bold text-foreground hover:bg-muted/20 transition-colors">
            <span>Beginning Cash Balance</span>
            <span className="font-mono text-base">{formatCurrency(beginningBalance)}</span>
          </div>

          {/* 2. Cash flow from operating activities (Accordion) */}
          <div>
            <div
              onClick={() => setIsOperatingOpen(!isOperatingOpen)}
              className="px-6 py-4 flex justify-between items-center font-bold text-foreground cursor-pointer hover:bg-muted/30 transition-colors"
            >
              <div className="flex items-center gap-2">
                {isOperatingOpen ? <ChevronDown className="w-4 h-4 text-muted-foreground" /> : <ChevronRight className="w-4 h-4 text-muted-foreground" />}
                <span>Cash flow from operating activities</span>
              </div>
              <span className="font-mono text-base">{formatCurrency(operatingTotal)}</span>
            </div>

            {isOperatingOpen && (
              <div className="bg-slate-50/60 dark:bg-slate-900/40 divide-y divide-border/40 text-xs">
                {/* Net Income/Loss */}
                <div className="px-10 py-2.5 flex justify-between items-center text-foreground font-medium">
                  <span>Net Income/Loss</span>
                  <span className="font-mono">{formatCurrency(netIncome)}</span>
                </div>

                {/* Adjustments for section */}
                <div className="px-10 py-2 font-semibold text-slate-800 dark:text-slate-200">Adjustments for</div>
                <div className="px-14 py-1.5 flex justify-between items-center text-muted-foreground">
                  <span>Depreciation and Amortization</span>
                  <span className="font-mono">{formatCurrency(depreciation)}</span>
                </div>
                <div className="px-14 py-1.5 flex justify-between items-center text-muted-foreground">
                  <span>Interest Expense</span>
                  <span className="font-mono">{formatCurrency(interestExpense)}</span>
                </div>
                <div className="px-14 py-1.5 flex justify-between items-center text-muted-foreground">
                  <span>Interest Income</span>
                  <span className="font-mono">{formatCurrency(interestIncome)}</span>
                </div>
                <div className="px-14 py-1.5 flex justify-between items-center text-muted-foreground">
                  <span>(Profit)/Loss on Sale of Fixed Assets</span>
                  <span className="font-mono">{formatCurrency(profitAssetSale)}</span>
                </div>

                {/* Operating Profit before Working Capital Changes */}
                <div className="px-10 py-2.5 flex justify-between items-center font-semibold text-foreground">
                  <span>Operating Profit before Working Capital Changes</span>
                  <span className="font-mono">{formatCurrency(opProfitBeforeWC)}</span>
                </div>

                {/* Working capital changes section */}
                <div className="px-10 py-2 font-semibold text-slate-800 dark:text-slate-200">Working capital changes</div>
                <div className="px-14 py-1.5 flex justify-between items-center text-muted-foreground">
                  <span>Inventory</span>
                  <span className="font-mono">{formatCurrency(inventory)}</span>
                </div>
                <div className="px-14 py-1.5 flex justify-between items-center text-muted-foreground">
                  <span>Receivables</span>
                  <span className="font-mono">{formatCurrency(receivables)}</span>
                </div>
                <div className="px-14 py-1.5 flex justify-between items-center text-muted-foreground">
                  <span>Payables</span>
                  <span className="font-mono">{formatCurrency(payables)}</span>
                </div>
                <div className="px-14 py-1.5 flex justify-between items-center text-muted-foreground">
                  <span>Tax Payable</span>
                  <span className="font-mono">{formatCurrency(taxPayable)}</span>
                </div>
                <div className="px-14 py-1.5 flex justify-between items-center text-muted-foreground">
                  <span>Tax Receivable</span>
                  <span className="font-mono">{formatCurrency(taxReceivable)}</span>
                </div>
                <div className="px-14 py-1.5 flex justify-between items-center text-muted-foreground">
                  <span>Provisions</span>
                  <span className="font-mono">{formatCurrency(provisions)}</span>
                </div>
                <div className="px-14 py-1.5 flex justify-between items-center text-muted-foreground">
                  <span>Other WC items</span>
                  <span className="font-mono">{formatCurrency(otherWC)}</span>
                </div>

                {/* Cash Generated from Operations */}
                <div className="px-10 py-2.5 flex justify-between items-center font-bold text-foreground bg-slate-100/50 dark:bg-slate-900/60">
                  <span>Cash Generated from Operations</span>
                  <span className="font-mono">{formatCurrency(cashFromOperations)}</span>
                </div>
              </div>
            )}
          </div>

          {/* 3. Cash flow from investing activities (Accordion) */}
          <div>
            <div
              onClick={() => setIsInvestingOpen(!isInvestingOpen)}
              className="px-6 py-4 flex justify-between items-center font-bold text-foreground cursor-pointer hover:bg-muted/30 transition-colors"
            >
              <div className="flex items-center gap-2">
                {isInvestingOpen ? <ChevronDown className="w-4 h-4 text-muted-foreground" /> : <ChevronRight className="w-4 h-4 text-muted-foreground" />}
                <span>Cash flow from investing activities</span>
              </div>
              <span className="font-mono text-base">{formatCurrency(investingTotal)}</span>
            </div>

            {isInvestingOpen && (
              <div className="bg-slate-50/60 dark:bg-slate-900/40 divide-y divide-border/40 text-xs">
                <div className="px-10 py-2 flex justify-between items-center text-muted-foreground">
                  <span>Purchase of Property, Plant & Equipment / Intangible Assets</span>
                  <span className="font-mono">{formatCurrency(ppePurchase)}</span>
                </div>
                <div className="px-10 py-2 flex justify-between items-center text-muted-foreground">
                  <span>Proceeds from Sale of Fixed Assets</span>
                  <span className="font-mono">{formatCurrency(ppeSale)}</span>
                </div>
                <div className="px-10 py-2 flex justify-between items-center text-muted-foreground">
                  <span>Purchase of Investments</span>
                  <span className="font-mono">{formatCurrency(investmentsPurchase)}</span>
                </div>
                <div className="px-10 py-2 flex justify-between items-center text-muted-foreground">
                  <span>Proceeds from Sale of Investments</span>
                  <span className="font-mono">{formatCurrency(investmentsSale)}</span>
                </div>
                <div className="px-10 py-2 flex justify-between items-center text-muted-foreground">
                  <span>Interest Received</span>
                  <span className="font-mono">{formatCurrency(interestReceived)}</span>
                </div>
                <div className="px-10 py-2 flex justify-between items-center text-muted-foreground">
                  <span>Dividend Received</span>
                  <span className="font-mono">{formatCurrency(dividendReceived)}</span>
                </div>
              </div>
            )}
          </div>

          {/* 4. Cash flow from financing activities (Accordion) */}
          <div>
            <div
              onClick={() => setIsFinancingOpen(!isFinancingOpen)}
              className="px-6 py-4 flex justify-between items-center font-bold text-foreground cursor-pointer hover:bg-muted/30 transition-colors"
            >
              <div className="flex items-center gap-2">
                {isFinancingOpen ? <ChevronDown className="w-4 h-4 text-muted-foreground" /> : <ChevronRight className="w-4 h-4 text-muted-foreground" />}
                <span>Cash flow from financing activities</span>
              </div>
              <span className="font-mono text-base">{formatCurrency(financingTotal)}</span>
            </div>

            {isFinancingOpen && (
              <div className="bg-slate-50/60 dark:bg-slate-900/40 divide-y divide-border/40 text-xs">
                <div className="px-10 py-2 flex justify-between items-center text-muted-foreground">
                  <span>Proceeds from Issue of Equity Shares / Share Capital</span>
                  <span className="font-mono">{formatCurrency(shareCapital)}</span>
                </div>
                <div className="px-10 py-2 flex justify-between items-center text-muted-foreground">
                  <span>Proceeds from Borrowings (Short-term / Long-term)</span>
                  <span className="font-mono">{formatCurrency(borrowingsProceeds)}</span>
                </div>
                <div className="px-10 py-2 flex justify-between items-center text-muted-foreground">
                  <span>Repayment of Borrowings</span>
                  <span className="font-mono">{formatCurrency(borrowingsRepayment)}</span>
                </div>
                <div className="px-10 py-2 flex justify-between items-center text-muted-foreground">
                  <span>Interest Paid</span>
                  <span className="font-mono">{formatCurrency(interestPaid)}</span>
                </div>
                <div className="px-10 py-2 flex justify-between items-center text-muted-foreground">
                  <span>Dividend Paid (including Dividend Distribution Tax)</span>
                  <span className="font-mono">{formatCurrency(dividendPaid)}</span>
                </div>
              </div>
            )}
          </div>

          {/* 5. Net Increase / (Decrease) in Cash and Cash Equivalents */}
          <div className="px-6 py-4 flex justify-between items-center font-bold text-foreground hover:bg-muted/20 transition-colors bg-slate-50/50 dark:bg-slate-900/30">
            <span>Net Increase / (Decrease) in Cash and Cash Equivalents</span>
            <span className="font-mono text-base">{formatCurrency(netIncreaseInCash)}</span>
          </div>

          {/* 6. Cash and Cash Equivalents at the End of the Period */}
          <div className="px-6 py-4 flex justify-between items-center font-bold text-foreground hover:bg-muted/20 transition-colors bg-slate-100/60 dark:bg-slate-900/60">
            <span>Cash and Cash Equivalents at the End of the Period</span>
            <span className="font-mono text-base text-purple-700 dark:text-purple-400">{formatCurrency(endingBalance)}</span>
          </div>
        </div>
      </div>
    </div>
  );
}
